import { collection, documentId, onSnapshot, query, where } from "firebase/firestore";
import { firestoreConverters } from "@foresting-tomorrow/converters";
import { Feature } from "geojson";
import { firestore as db } from "lib/firebase/firebase";
import { MapProps } from "types/estatesAndMap";

export type AudienceMapState = {
  status: "loading" | "ready" | "unavailable";
  map: MapProps | null;
  features: Record<string, Feature>;
};

// Only these fields reach the audience UI. Direct database access still needs rules.
export function toAudienceMap(id: string, data: Record<string, any>): MapProps | null {
  if (data.audienceActive !== true || data.status !== "current") return null;
  const mapFeature = data.mapFeature
    ? (firestoreConverters.fromFirestoreFeatureToGeoJson({
        type: data.mapFeature.type,
        coordinates: data.mapFeature.coordinates,
        bbox: data.mapFeature.bbox ?? data.bbox,
        id,
        title: data.name,
        categories: ["audience"],
        status: "current",
        creationDate: "",
        lastModifiedDate: "",
      }) as unknown as Feature)
    : undefined;
  return {
    id,
    name: data.name,
    audienceActive: true,
    hasAudienceContent: data.hasAudienceContent === true,
    status: "current",
    categories: ["audience"],
    users: [],
    roles: {},
    estateId: "",
    estateName: "",
    creationDate: "",
    lastModifiedDate: "",
    bbox: data.bbox,
    mapFeature,
  };
}

export function toAudienceFeature(
  id: string,
  mapId: string,
  data: Record<string, any>,
): Feature | null {
  if (
    data.includeInAudience !== true ||
    data.status !== "current" ||
    !Array.isArray(data.categories) ||
    !data.categories.includes("audience") ||
    data.categories.some((category: string) => category === "private" || category === "internal")
  )
    return null;
  const { type, coordinates, title, section, bbox, mainSpecies, areaBearing, featureMetric, renderPlacement } = data;
  const styleKeys = [
    "pointIcon",
    "lineStyle",
    "lineWidth",
    "lineColor",
    "polygonStyle",
    "fillColor",
    "fillOpacity",
    "radius",
  ];
  const styleObject =
    data.styleObject &&
    Object.fromEntries(Object.entries(data.styleObject).filter(([key]) => styleKeys.includes(key)));
  return firestoreConverters.fromFirestoreFeatureToGeoJson({
    id,
    mapId,
    type,
    coordinates,
    title,
    section,
    bbox,
    mainSpecies,
    areaBearing,
    featureMetric,
    renderPlacement,
    styleObject,
    categories: [
      ...new Set<string>(
        data.categories.filter(
          (category: unknown): category is string => typeof category === "string",
        ),
      ),
    ],
    status: "current",
    creationDate: "",
    lastModifiedDate: "",
  }) as unknown as Feature;
}

/** One parent listener owns the feature listener, including live revocation and cleanup. */
export function subscribeAudienceMap(
  mapId: string,
  emit: (state: AudienceMapState) => void,
): () => void {
  let active = true;
  let generation = 0;
  let featureUnsubscribe: (() => void) | undefined;
  let currentMap: MapProps | null = null;
  let features: Record<string, Feature> = {};
  let featuresReady = false;
  const publish = () => {
    // Discover categories from approved public features, never from the full
    // map metadata, which can include categories used only by private features.
    const map = currentMap
      ? {
          ...currentMap,
          categories: [
            ...new Set<string>([
              "audience",
              ...Object.values(features).flatMap((feature) => feature.properties?.categories ?? []),
            ]),
          ],
        }
      : null;
    if (active)
      emit({
        status: currentMap ? (featuresReady ? "ready" : "loading") : "unavailable",
        map,
        features,
      });
  };
  const clear = () => {
    generation++;
    featureUnsubscribe?.();
    featureUnsubscribe = undefined;
    currentMap = null;
    features = {};
    featuresReady = false;
    publish();
  };
  emit({ status: "loading", map: null, features: {} });
  const parentQuery = query(
    collection(db, "maps"),
    where(documentId(), "==", mapId),
    where("audienceActive", "==", true),
    where("status", "==", "current"),
  );
  const parentUnsubscribe = onSnapshot(
    parentQuery,
    { includeMetadataChanges: true },
    (snapshot) => {
      if (!active || snapshot.metadata.fromCache) return;
      try {
        const document = snapshot.docs[0];
        currentMap = document ? toAudienceMap(document.id, document.data()) : null;
        if (!currentMap) {
          clear();
          return;
        }
        publish();
        if (featureUnsubscribe) return;
        const token = ++generation;
        const featureQuery = query(
          collection(db, `maps/${mapId}/features`),
          where("includeInAudience", "==", true),
          where("status", "==", "current"),
        );
        featureUnsubscribe = onSnapshot(
          featureQuery,
          { includeMetadataChanges: true },
          (featureSnapshot) => {
            if (!active || token !== generation || featureSnapshot.metadata.fromCache) return;
            try {
              const next: Record<string, Feature> = {};
              featureSnapshot.docs.forEach((document) => {
                const feature = toAudienceFeature(document.id, mapId, document.data());
                if (feature) next[document.id] = feature;
              });
              features = next;
              featuresReady = true;
              publish();
            } catch {
              clear();
            }
          },
          () => {
            if (active && token === generation) clear();
          },
        );
      } catch {
        clear();
      }
    },
    () => {
      if (active) clear();
    },
  );
  return () => {
    active = false;
    generation++;
    parentUnsubscribe();
    featureUnsubscribe?.();
  };
}
